import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../../state'

/** Input component for DMS Files values (documents attached to a record). */
@ngCore.Component({
  imports: [ds.button.Component, ds.input.file.Component],
  selector: 'atn-files-input',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.kbDocumentUploads.Feature} */
  public readonly kbFeature = ngCore.inject(state.kbDocumentUploads.Feature)

  /** @see {@link ents.core.dms.data.Value.Type.Files} */
  public readonly typeSg =
    ngCore.input.required<ents.core.dms.data.Value.Type.Files>({
      alias: 'type',
    })
  /** Optional label shown above the input */
  public readonly labelSg = ngCore.input<string>(undefined, {
    alias: 'label',
  })
  /** Whether at least one file is required */
  public readonly requiredSg = ngCore.input<boolean>(false, {
    alias: 'required',
  })
  /** File names already persisted on the record's attribute value */
  public readonly valueSg = ngCore.model<readonly string[] | undefined>(
    undefined,
    { alias: 'value' },
  )
  /**
   * Identifies the pending selection tracked in {@link state.kbDocumentUploads.Feature}
   * for this attribute. Stable across create and edit modes. `undefined`
   * disables file selection (the caller failed to supply a key).
   *
   * @see {@link state.dms.records.documentKeyFor}
   */
  public readonly documentKeySg = ngCore.input<
    state.kbDocumentUploads.DocumentKey | undefined
  >(undefined, { alias: 'documentKey' })
  /**
   * Audience used to build download/delete targets for already-persisted
   * files. `undefined` while the record doesn't exist yet (create mode) —
   * uploads for newly-picked files only happen once the record is saved.
   */
  public readonly recordTargetSg = ngCore.input<
    ents.core.agent.KnowledgeBase.Target.Record | undefined
  >(undefined, { alias: 'recordTarget' })

  /** Files picked but not yet uploaded, staged in shared upload state. */
  public readonly pendingFilesSg = ngCore.computed(
    (key = this.documentKeySg()) =>
      key ? (this.kbFeature.selectors.selection(key).asSignal() ?? []) : [],
  )

  /** Already-persisted file names shown alongside pending selections. */
  public readonly existingFileNamesSg = ngCore.computed(
    (value = this.valueSg()) => value ?? [],
  )

  constructor() {
    // Reacts to state rather than optimistically mutating valueSg on click.
    ngCore.effect(() => {
      for (const name of this.existingFileNamesSg()) {
        const key = this.perFileKey(name)
        if (key && this.kbFeature.selectors.deleteSuccess(key).asSignal()) {
          this.valueSg.update(names =>
            (names ?? []).filter(existingName => existingName !== name),
          )
        }
      }
    })
  }

  /** Stages a newly-picked file for upload once the record is saved. */
  public addFileHandler(file: File | undefined): void {
    const key = this.documentKeySg()
    if (!file || !key) {
      return
    }
    this.kbFeature.actions.select
      .from('AtnFilesInput.File.Selected')
      .dispatch({ key, files: [...this.pendingFilesSg(), file] })
  }

  /** Removes one not-yet-uploaded file from the pending selection. */
  public removePendingFileHandler(file: File): void {
    const key = this.documentKeySg()
    if (!key) {
      return
    }
    const files = this.pendingFilesSg().filter(pending => pending !== file)
    if (files.length > 0) {
      this.kbFeature.actions.select
        .from('AtnFilesInput.PendingFile.Removed')
        .dispatch({ key, files })
    } else {
      this.kbFeature.actions.clearSelection
        .from('AtnFilesInput.PendingFile.Removed')
        .dispatch({ key })
    }
  }

  /** Requests a temporary download URL for one already-persisted file. */
  public downloadClickHandler(name: string): void {
    const target = this._getFileTarget(name)
    const key = this.perFileKey(name)
    if (!target || !key) {
      return
    }
    this.kbFeature.actions.download
      .from('AtnFilesInput.Download.Clicked')
      .dispatch({ key, target })
  }

  /**
   * Requests deletion of one already-persisted file; {@link valueSg} is
   * trimmed reactively once state confirms the deletion succeeded.
   */
  public deleteClickHandler(name: string): void {
    const target = this._getFileTarget(name)
    const key = this.perFileKey(name)
    if (!target || !key) {
      return
    }
    this.kbFeature.actions.delete
      .from('AtnFilesInput.Delete.Clicked')
      .dispatch({ key, target })
  }

  /** Distinguishes each already-persisted file's own operation state. */
  public perFileKey(
    name: string,
  ): state.kbDocumentUploads.DocumentKey | undefined {
    const key = this.documentKeySg()
    return key ? `${key}:${name}` : undefined
  }

  /** Builds the {@link ents.core.agent.KnowledgeBase.Management.FileTarget} for one already-persisted file. */
  private _getFileTarget(
    name: string,
  ): ents.core.agent.KnowledgeBase.Management.FileTarget | undefined {
    const target = this.recordTargetSg()
    if (!target) {
      return undefined
    }
    return {
      dataSourceId:
        ents.core.agent.KnowledgeBase.Management.SHARED_SCOPED_DATA_SOURCE_ID,
      key: ents.core.agent.KnowledgeBase.Management.Key.from({ name, target }),
      knowledgeBaseId: ents.core.agent.KnowledgeBase.SharedScoped,
    }
  }
}
